import { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../../utils/api';
import { errorText } from '../../utils/errorText';
import { resolvePhotoUrl } from '../../utils/photoUrl';
import { CULTURE_CARD_REASON, MICRO_STRUCTURES } from '../../constants/microStructures';
import MicroscopyViewer from '../../pages/scan/MicroscopyViewer';
import OrganicLoader from '../organic/OrganicLoader.jsx';
import * as S from '../specimens/styles';

/*
 * The culture card's micrographs: every microscopy image linked to one of
 * the isolate's plates, to its collection or intake record, or to its strain
 * (the 2026-10 archive ingest linked its ~90 micrographs to the origin and
 * the strain, never to a plate), read through GET /microscopy?kind=&id= --
 * one request per target, de-duplicated by image, each image labelled by
 * where it is linked. Grouped by what it shows (microscopy_images.structure,
 * 0066) in MICRO_STRUCTURES' order, untyped last, with a count per group.
 *
 * A thumbnail opens the station's viewer read-only (the scale bar, where the
 * scale came from, the optics and the links). Someone who may register a
 * micrograph (lab:observe) types each one from a select here, so an untyped
 * archive micrograph is classified where it is seen; the server records the
 * change with CULTURE_CARD_REASON.
 *
 * A 403 (no lab:read) hides the section behind one line; any other failure
 * says so and offers a retry. `reloadKey` changes when the dossier is read
 * again (after an upload from the quick drawer), and the list is read again.
 */
export function micrographTargets(item, plates) {
  const out = plates.map((p) => ({ kind: 'plate', id: p.plate_id, label: p.plate_code }));
  if (!item.redacted) {
    if (item.sample_id) out.push({ kind: 'sample', id: item.sample_id, label: null, origin: true });
    else if (item.external_id) out.push({ kind: 'external', id: item.external_id, label: null, origin: true });
  }
  if (item.strain_id) out.push({ kind: 'strain', id: item.strain_id, label: item.strain_code || null, strain: true });
  return out;
}

/** The structure groups in display order, only those with images; untyped last. */
export function groupByStructure(images) {
  const order = [...MICRO_STRUCTURES, null];
  return order
    .map((structure) => ({ structure, images: images.filter((i) => (i.structure || null) === structure) }))
    .filter((g) => g.images.length > 0);
}

export default function CultureMicrographs({ item, plates, canType, t, reloadKey }) {
  const [images, setImages] = useState([]);
  const [state, setState] = useState('loading');
  const [attempt, setAttempt] = useState(0);
  const [viewing, setViewing] = useState(null);
  const [typeError, setTypeError] = useState({});
  const targets = useMemo(() => micrographTargets(item, plates), [item, plates]);
  const targetKey = targets.map((x) => `${x.kind}:${x.id}`).join(',');

  useEffect(() => {
    let alive = true;
    if (!targets.length) { setImages([]); setState('ready'); return undefined; }
    setState((s) => (s === 'ready' ? 'ready' : 'loading'));
    Promise.allSettled(targets.map((x) => api.get('/microscopy', { params: { kind: x.kind, id: x.id } })))
      .then((results) => {
        if (!alive) return;
        const refused = results.find((r) => r.status === 'rejected');
        if (refused) {
          setState(refused.reason?.response?.status === 403 ? 'forbidden' : `failed:${errorText(refused.reason)}`);
          return;
        }
        const byId = new Map();
        results.forEach((r, i) => {
          for (const image of Array.isArray(r.value?.data) ? r.value.data : []) {
            const seen = byId.get(image.image_id) || { ...image, places: [] };
            seen.places.push(targets[i]);
            byId.set(image.image_id, seen);
          }
        });
        setImages([...byId.values()]);
        setState('ready');
      });
    return () => { alive = false; };
    // targetKey names the targets; reloadKey and attempt ask again.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetKey, reloadKey, attempt]);

  const place = useCallback((p) => (p.origin ? t('peek.culture.micrographs.origin')
    : p.strain ? [t('peek.culture.micrographs.strain'), p.label].filter(Boolean).join(' ') : p.label), [t]);
  const structureLabel = (s) => t(`scan:structure.${s || 'untyped'}`);

  const setType = async (image, value) => {
    setTypeError((e) => ({ ...e, [image.image_id]: '' }));
    try {
      const { data } = await api.post(`/microscopy/${image.image_id}/structure`,
        { structure: value || null, reason: CULTURE_CARD_REASON });
      setImages((all) => all.map((i) => (i.image_id === image.image_id ? { ...i, ...data, places: i.places } : i)));
      setViewing((v) => (v && v.image_id === image.image_id ? { ...v, ...data } : v));
    } catch (e) {
      setTypeError((errs) => ({ ...errs, [image.image_id]: `${t('peek.culture.micrographs.typeFailed')} ${errorText(e)}` }));
    }
  };

  if (state === 'loading') return <OrganicLoader block label={t('common:loading')} slowLabel={t('common:stillWorking')} />;
  if (state === 'forbidden') return <p style={S.hint} data-testid="hub-peek-micro-forbidden">{t('peek.culture.micrographs.notVisible')}</p>;
  if (state.startsWith('failed:')) {
    return (
      <div role="alert" style={S.error} data-testid="hub-peek-micro-failed">
        {t('peek.culture.micrographs.loadFailed')} {state.slice(7)}{' '}
        <button type="button" style={S.ghostBtn} onClick={() => setAttempt((n) => n + 1)}>{t('retry')}</button>
      </div>
    );
  }
  if (!images.length) return <p style={S.hint}>{t('peek.culture.micrographs.none')}</p>;

  return (
    <div data-testid="hub-peek-micrographs">
      {groupByStructure(images).map((group) => (
        <section key={group.structure || 'untyped'} data-testid={`hub-peek-micro-group-${group.structure || 'untyped'}`}>
          <h5 style={{ fontSize: 13, margin: '10px 0 4px' }}>
            {t('peek.culture.micrographs.group', { label: structureLabel(group.structure), n: group.images.length })}
          </h5>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            {group.images.map((image) => (
              <figure key={image.image_id} style={{ margin: 0, width: 120 }} data-testid={`hub-peek-micro-${image.image_id}`}>
                <button type="button" onClick={() => setViewing(image)} title={t('peek.culture.micrographs.open')}
                  aria-label={t('peek.culture.micrographs.open')} data-testid={`hub-peek-micro-open-${image.image_id}`}
                  style={{ width: 120, height: 90, padding: 0, border: '1px solid var(--c-border)', background: 'var(--c-surface)', cursor: 'pointer' }}>
                  <img src={resolvePhotoUrl(image.thumbnail_url || image.url)} alt={structureLabel(image.structure)} loading="lazy"
                    style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                </button>
                <figcaption style={{ fontSize: 11, color: 'var(--c-text-muted-fg)', overflowWrap: 'anywhere' }}>
                  <bdi dir="ltr">{image.places.map(place).filter(Boolean).join(' · ')}</bdi>
                  {image.magnification ? ` · ${image.magnification}×` : ''}
                </figcaption>
                {canType && (
                  <select value={image.structure || ''} onChange={(e) => setType(image, e.target.value)}
                    style={{ ...S.input, fontSize: 12, padding: '2px 4px', width: '100%' }}
                    title={t('peek.culture.micrographs.structure')} aria-label={t('peek.culture.micrographs.structure')}
                    data-testid={`hub-peek-micro-type-${image.image_id}`}>
                    <option value="">{structureLabel(null)}</option>
                    {MICRO_STRUCTURES.map((s) => <option key={s} value={s}>{structureLabel(s)}</option>)}
                  </select>
                )}
                {typeError[image.image_id] && <p role="alert" style={{ ...S.error, fontSize: 11 }}>{typeError[image.image_id]}</p>}
              </figure>
            ))}
          </div>
        </section>
      ))}
      {viewing && (
        <div style={{ marginTop: 10 }} data-testid="hub-peek-micro-viewer">
          <button type="button" style={S.ghostBtn} onClick={() => setViewing(null)} title={t('peek.culture.micrographs.closeViewer')}>
            {t('peek.culture.micrographs.closeViewer')}
          </button>
          <MicroscopyViewer key={`${viewing.image_id}-${viewing.structure || ''}`} image={viewing} readOnly />
        </div>
      )}
    </div>
  );
}
